'use client';
import { config } from '@/apollo/config';
import { BRAND_LIST_QUERY } from '@/apollo/graphql/queries/brandQueries/brandQueries';
import { decodeBase64Id } from '@/helpers/decodeBase64Id';
import { useLanguageCode } from '@/hooks/useLanguageCode';
import { Link } from '@/i18n/navigation';
import { useQuery } from '@apollo/client';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FaAngleRight } from 'react-icons/fa6';
import Skeleton from 'react-loading-skeleton';
import styles from './popular-brands.module.scss';
import DefaultProductPlaceholder from '@/assets/images/Yellow.png';

type BrandType = {
  node: {
    id: string;
    name: string;
    image: string;
    slug?: string;
  };
};

const PopularBrands = ({
  className,
  size = 6,
}: {
  className?: string;
  size?: number;
}) => {
  const languageCode = useLanguageCode();
  const { data, loading } = useQuery(BRAND_LIST_QUERY, {
    variables: { first: size, languageCode: languageCode },
  });
  const t = useTranslations('General');

  const brands = data?.brands?.edges || [];

  const extendedBrands: BrandType[] = [
    ...brands,
    {
      node: {
        id: 'all-brands',
        name: t('all-brands'),
        image: '/',
      },
      __typename: 'BrandCountableEdge',
    },
  ];

  return brands && brands.length > 0 ? (
    <div className={styles.carouselPopularBrands}>
      <div className={styles.customHeader}>
        <h2 className={styles.title}>{t('popular-brands')}</h2>
      </div>

      <div className={styles.brandsWrapper}>
        {loading
          ? Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className={`${styles.brand} ${styles.brand6} ${styles.skeleton}`}
              >
                <Skeleton
                  height={160}
                  style={{ borderRadius: '16px', marginBottom: '8px' }}
                />
              </div>
            ))
          : extendedBrands.map(({ node }) => (
              <Link
                key={node.id}
                href={
                  node.id === 'all-brands'
                    ? '/brand'
                    : `/brand/${decodeBase64Id(node.id)}-${node.slug}`
                }
                className={`${className} ${styles.brand}`}
              >
                {node.id === 'all-brands' ? (
                  <div className={styles.allBrandBox}>
                    {node.name} <FaAngleRight className={styles.arrow} />
                  </div>
                ) : (
                  <Image
                    src={
                      node.image
                        ? `${config.publicApiUrl}${node.image}`
                        : DefaultProductPlaceholder
                    }
                    alt={node.name}
                    className={styles.brandLogo}
                    width={90}
                    height={40}
                  />
                )}
              </Link>
            ))}
      </div>
    </div>
  ) : null;
};

export default PopularBrands;
